import React, { useEffect } from 'react'
import { useSpring, animated } from '@react-spring/web'
import { styled } from 'styled-components'
import type { VocalTrack } from '@mission.io/mission-toolkit'
import { useResolvers } from '../../context'
import {
	useIsCharacterOverlay,
	useCurrentASLVideo,
	useLocalDispatch,
	useCurrentVocalTrack,
	addASLVideo,
	resetASLVideos,
	removeASLVideo,
} from '../../context'
import type { ASLVideo } from '../../utility'

const getASLVersionOfVocalTrack = (vocalTrack: ?VocalTrack): ?ASLVideo => {
	if (vocalTrack?.accessibilityUrl) {
		return {
			id: vocalTrack._actionId,
			url: vocalTrack.accessibilityUrl,
		}
	}
	return null
}

export default function Accessibility({
	showASLVideo,
}: {
	showASLVideo: boolean,
}): React$Node {
	const { videoSourceResolver } = useResolvers()
	const localDispatch = useLocalDispatch()
	const currentVocalTrack = useCurrentVocalTrack()
	const currentASLVideo = useCurrentASLVideo()
	const isCharacterOverlay = useIsCharacterOverlay()
	useEffect(() => {
		const newASlVideo = getASLVersionOfVocalTrack(currentVocalTrack)
		if (newASlVideo) {
			localDispatch(addASLVideo(newASlVideo))
		}
	}, [currentVocalTrack, localDispatch])

	useEffect(() => {
		return () => {
			// on un-mount we want to remove all ASL videos
			localDispatch(resetASLVideos())
		}
	}, [localDispatch])

	const aslVideoStyles = useSpring({
		width: showASLVideo ? (isCharacterOverlay ? '50%' : '100%') : '0%',
		height: showASLVideo ? '87%' : '0%',
	})

	const renderCaptions = () => {
		return (
			currentVocalTrack &&
			!isCharacterOverlay && (
				<CaptionDiv>
					<p>{currentVocalTrack?.caption}</p>
				</CaptionDiv>
			)
		)
	}

	return (
		<>
			{renderCaptions()}
			{currentASLVideo && (
				<AnimatedVideo
					style={aslVideoStyles}
					autoPlay
					muted={true}
					onEnded={() => {
						localDispatch(removeASLVideo(currentASLVideo.id))
					}}
					src={videoSourceResolver(currentASLVideo.url)}
				/>
			)}
		</>
	)
}

const CaptionDiv = styled.div`
	position: absolute;
	display: flex;
	justify-content: center;
	align-items: center;
	text-align: center;

	${({ theme }) => `
		font-size: ${theme.fontxl};
		font-weight: ${theme.fontBold};
		padding: ${theme.spacing4x};
		background-color: ${theme.base4};
		color: ${theme.onBase};
		z-index:  ${theme.zTopOverlay};
	`}

	height: 10%;
	width: 100%;
	bottom: 13%;

	p {
		margin: 0;
	}
`

const StyledVideo = styled.video`
	position: absolute;
	right: 0;
	top: 0;
	z-index: ${({ theme }) => theme.zOverlay};
`
const AnimatedVideo = animated(StyledVideo)
